import { Link } from 'react-router-dom';

import UndoIcon from '@mui/icons-material/Undo';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Avatar, Box, Button } from '@mui/material';

import dayjs from 'dayjs';
import { MRT_ColumnDef } from 'material-react-table';

import {
  UserAccessRequestResultValue,
  VennueAccessRequestItem,
} from '../../../../graphql/API';
import LinkProfile from '../../../../pages/Collabs/components/LinkProfile';
import Icons from '../../../../themes/icons';
import StatusLabel from '../../../StatusLabel/StatusLabel';
import { ReservationIcon } from '../../../Icons';

type ProductRequestsTableColumnsOptions = {
  onUndo?: (row: VennueAccessRequestItem) => void;
  onView?: (row: VennueAccessRequestItem) => void;
};

type AccessRequestWithReservation = VennueAccessRequestItem & { reservationID?: string | null };

export const productRequestsTableColumns = (
  options: ProductRequestsTableColumnsOptions = {},
): MRT_ColumnDef<VennueAccessRequestItem>[] => {
  const { onUndo, onView } = options;

  return [
  {
    accessorKey: 'user.picture.thumbnail',
    header: '',
    size: 20,
    enableSorting: false,
    Cell: ({ row }) => (
      <Avatar
        src={String(row.original.user?.picture?.thumbnail)}
        alt={row.original.user?.firstname || ''}
        sx={{ height: '40px', width: '40px' }}
      />
    ),
  },
  {
    accessorKey: 'userName',
    header: 'User',
    size: 200,
    enableSorting: true,
    Cell: ({
      row: {
        original: { user, userID, offer },
      },
    }) => (
      <Box display={'flex'} alignItems={'center'} gap='6px'>
        <LinkProfile
          fontSize='15px'
          url={`/users/${userID}`}
          label={`${user?.firstname} ${user?.lastname}`}
          hoverTextDecoration='underline'
        />
        {offer?.incentiveValue ? <Icons.DollarSignV2Icon /> : null}
      </Box>
    ),
  },
  {
    accessorKey: 'offerTitle',
    header: 'Product',
    size: 200,
    Cell: ({ row }) => (
      <Box
        component={Link}
        sx={{
          color: '#111',
          textDecoration: 'none',
          ':hover': {
            textDecoration: 'underline',
          },
        }}
        to={`/offers/${row.original.offerID}`}
      >
        {row.original.offer?.title}
      </Box>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Date',
    size: 120,
    Cell: ({ row }) => dayjs.utc(row.original.createdAt).format('ll'),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 180,
    muiTableHeadCellProps: {
      align: 'center',
    },
    muiTableBodyCellProps: {
      align: 'center',
    },
    muiTableFooterCellProps: {
      align: 'center',
    },
    Cell: ({ row }) => (
      <StatusLabel type='accessRequestStatus' status={row.original.status} emptyBackground />
    ),
  },
  {
    id: 'action',
    header: 'Action',
    size: 120,
    enableSorting: false,
    muiTableHeadCellProps: {
      align: 'center',
    },
    muiTableBodyCellProps: {
      align: 'center',
    },
    Cell: ({ row }) => {
      const item = row.original;
      const status = item.status;
      const isRejected = status === UserAccessRequestResultValue.declined;
      const isAccepted = status === UserAccessRequestResultValue.accepted;
      const isArchived = status === UserAccessRequestResultValue.archived;

      const updatedAt = item.updatedAt ?? item.createdAt;
      const twoWeeksFromUpdatedAt = updatedAt && dayjs.utc(updatedAt).add(2, 'week');
      const isWithinTwoWeeks = twoWeeksFromUpdatedAt && dayjs.utc().isBefore(twoWeeksFromUpdatedAt);

      if (isRejected && onUndo && isWithinTwoWeeks) {
        return (
          <Button
            size='small'
            variant='outlined'
            startIcon={<UndoIcon sx={{ fontSize: '18px' }} />}
            onClick={() => onUndo?.(item)}
            sx={{
              backgroundColor: '#F9F9F9',
              height: '39px',
              width: '147px',
              fontSize: '15px',
              fontWeight: '400',
              borderColor: '#E0E0E0',
              color: '#000',
              '&:hover': {
                backgroundColor: '#F9F9F9',
                borderColor: '#E0E0E0',
              },
            }}
          >
            Undo
          </Button>
        );
      }

      if (isAccepted || isArchived) {
        return (
          <Button
            size='small'
            variant='contained'
            startIcon={<ReservationIcon sx={{ fontSize: 26 }} aria-label='reservations' />}
            onClick={() => onView?.(item)}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              height: '39px',
              width: '147px',
              fontSize: '15px',
              fontWeight: '400',
              '&:hover': {
                backgroundColor: '#333',
              },
            }}
          >
            View
          </Button>
        );
      }

      return null;
    },
  },
];
};
